import {
  Alert,
  Button,
  Group,
  Input,
  Loader,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MigrationStatus, useCompanies } from 'src/secret-menu/hooks/useCompanies';
import {
  RecoveryScope,
  ReprocessEmptyWrangledResponse,
  useReprocessEmptyWrangled,
} from 'src/secret-menu/reprocess-wrangled/hooks/useReprocessEmptyWrangled';

export default function ReprocessWrangledTab() {
  const { reprocess, isReprocessing } = useReprocessEmptyWrangled();
  const { companies } = useCompanies();
  const [result, setResult] = useState<ReprocessEmptyWrangledResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantOptions = (companies ?? [])
    .filter(company => company.migrationStatus === MigrationStatus.COMPLETED)
    .map(company => ({
      value: company.tenantName,
      label: `${company.name} (${company.tenantName})`,
    }))
    .sort((first, second) => first.label.localeCompare(second.label));

  const form = useForm({
    initialValues: {
      scope: 'all_tenants' as RecoveryScope,
      tenantName: '',
      fileId: '',
      sinceDate: '2026-08-03',
      delaySeconds: 1,
    },
  });

  const { scope } = form.values;

  const sinceOrNull = () => {
    const since = form.values.sinceDate.trim();
    return since ? since : null;
  };

  const runRequest = async (payload: Parameters<typeof reprocess>[0]) => {
    setError(null);
    setResult(null);
    try {
      const response = await reprocess(payload);
      setResult(response.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    }
  };

  const submit = async (dryRun: boolean) => {
    const tenantName = form.values.tenantName.trim();
    const fileId = form.values.fileId.trim();
    if (scope !== 'all_tenants' && !tenantName) {
      setError('Tenant is required for tenant and file scope.');
      return;
    }
    if (scope === 'file' && !fileId) {
      setError('File ID is required for file scope.');
      return;
    }
    await runRequest({
      scope,
      tenantName: scope === 'all_tenants' ? undefined : tenantName,
      fileId: scope === 'file' ? fileId : undefined,
      sinceDate: sinceOrNull(),
      delaySeconds: form.values.delaySeconds,
      dryRun,
    });
  };

  // Reads the current backlog across every tenant. Stateless, so it works even after leaving and
  // returning to the page while a run is still draining in the background.
  const checkStatus = () =>
    runRequest({ scope: 'all_tenants', sinceDate: sinceOrNull(), dryRun: true });

  return (
    <Stack gap="lg" p="md" bg="white">
      <Stack gap={4}>
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
          Reprocess empty-wrangled files
        </Text>
        <Text size="textMd" c="gray.6">
          Recovery for files with rich parsed content but an empty wrangle (the 3 Aug model outage).
          Preview counts affected files; Run re-wrangles and re-detects them in the background.
        </Text>
      </Stack>

      <Paper bg="red.6" radius="md" p="lg">
        <Group align="center" gap="lg" wrap="nowrap">
          <IconAlertTriangleFilled size={64} color="white" style={{ flexShrink: 0 }} />
          <Stack gap={4}>
            <Text c="white" fw={700} size="lg">
              Do not run during operating hours
            </Text>
            <Text c="white" size="sm">
              A run re-enqueues detection for every affected file onto the same queue that live
              uploads use, so a large run can slow document processing for users. Enqueuing is
              paced, but run it outside operating hours (roughly 8am to 6pm) to be safe.
            </Text>
          </Stack>
        </Group>
      </Paper>

      <Group align="flex-start" gap="lg" wrap="wrap">
        <Paper withBorder radius="md" p="lg" w={440} maw="100%">
          <Stack gap="md">
            <Input.Wrapper label="Scope">
              <SegmentedControl
                fullWidth
                data={[
                  { label: 'All tenants', value: 'all_tenants' },
                  { label: 'One tenant', value: 'tenant' },
                  { label: 'One file', value: 'file' },
                ]}
                {...form.getInputProps('scope')}
              />
            </Input.Wrapper>

            {scope !== 'all_tenants' && (
              <Select
                label="Tenant"
                placeholder="Select a tenant"
                data={tenantOptions}
                searchable
                {...form.getInputProps('tenantName')}
              />
            )}
            {scope === 'file' && (
              <TextInput
                label="File ID"
                placeholder="data_upload_file id"
                {...form.getInputProps('fileId')}
              />
            )}

            <Group grow>
              <TextInput
                label="Processed since"
                description="Blank for no limit"
                placeholder="YYYY-MM-DD"
                {...form.getInputProps('sinceDate')}
              />
              <NumberInput
                label="Delay (s)"
                description="Between files"
                min={0}
                step={0.5}
                {...form.getInputProps('delaySeconds')}
              />
            </Group>

            <Group gap="sm" mt="xs">
              <Button
                variant="default"
                onClick={() => submit(true)}
                disabled={isReprocessing}
                leftSection={isReprocessing ? <Loader size="xs" /> : undefined}
              >
                Preview
              </Button>
              <Button
                color="red"
                onClick={() => submit(false)}
                disabled={isReprocessing}
                leftSection={isReprocessing ? <Loader size="xs" /> : undefined}
              >
                Run recovery
              </Button>
            </Group>
          </Stack>
        </Paper>

        <Stack gap="sm" flex={1} miw={320}>
          <Group justify="space-between" align="center">
            <Text fw={FONT_WEIGHTS.semibold}>Status</Text>
            <Button
              variant="default"
              size="sm"
              onClick={checkStatus}
              disabled={isReprocessing}
              leftSection={isReprocessing ? <Loader size="xs" /> : undefined}
            >
              Check all tenants
            </Button>
          </Group>

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          {result?.started && (
            <Alert color="green" title="Recovery started">
              Re-enqueuing {result.totalCandidates} file(s). Detection re-runs in the background;
              use Check all tenants to watch the backlog drain.
            </Alert>
          )}

          {!result && !error && (
            <Text size="textSm" c="gray.6">
              Preview a scope, or check all tenants, to see the affected files here.
            </Text>
          )}

          {result &&
            (result.totalCandidates === 0 ? (
              <Text size="textSm" c="gray.6">
                No affected files.
              </Text>
            ) : (
              <Stack gap="xs">
                <Text size="textSm" c="gray.6">
                  {result.totalCandidates} affected file(s) across {result.tenants.length}{' '}
                  tenant(s).
                </Text>
                <Table withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Tenant</Table.Th>
                      <Table.Th>Affected files</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {result.tenants.map(tenant => (
                      <Table.Tr key={tenant.tenantName}>
                        <Table.Td>{tenant.tenantName}</Table.Td>
                        <Table.Td>{tenant.count}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            ))}
        </Stack>
      </Group>
    </Stack>
  );
}
